import { RelationshipType } from "./relationship";
import { RoleType } from "./role";

export enum MemberType {
  HUMAN = "h",
  PET = "p",
}

export enum DerivedMemberType {
  A = "A",
  C = "C",
  P = "P",
}

export enum GenderType {
  MALE = 1,
  FEMALE = 2,
  OTHER = 3,
}

export const genderOptions = [
  { label: "Male", value: GenderType.MALE },
  { label: "Female", value: GenderType.FEMALE },
  { label: "Prefer not to say", value: GenderType.OTHER },
];

export enum PetType {
  DOG = "dog",
  CAT = "cat",
}

// Age threshold for considering a member as a child (in years)
export const CHILD_AGE_THRESHOLD = 18;

/**
 * Determines if a member is a child based on their date of birth
 * @param member The member object or date of birth components
 * @returns boolean indicating if the member is a child
 */
export function isChild(
  member:
    | Member
    | {
        dob_year?: number;
        dob_month?: number;
        dob_day?: number;
        dobYear?: number;
        dobMonth?: number;
        dobDay?: number;
      }
    | null
    | undefined
): boolean {
  if (!member) return false;
  // Support both camelCase and snake_case
  const dobYear = (member as any).dobYear ?? (member as any).dob_year;
  const dobMonth = (member as any).dobMonth ?? (member as any).dob_month;
  const dobDay = (member as any).dobDay ?? (member as any).dob_day;

  if (!dobYear || !dobMonth) {
    return false;
  }

  const today = new Date();
  const birthDate = new Date(
    dobYear,
    dobMonth - 1, // JavaScript months are 0-indexed
    dobDay || 1 // Default to 1st of the month if day is not provided
  );

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust age if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < (dobDay || 1))) {
    age--;
  }

  return age < CHILD_AGE_THRESHOLD;
}

export enum MemberStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export interface Member {
  id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  dob_month?: number;
  dob_year?: number;
  dob_day?: number;
  email: string;
  dialCode: string;
  mobile: string;
  status: MemberStatus;
  lastLogin: string;
  roleType?: RoleType;
  member_type?: MemberType; // 'H' for human, 'P' for pet
  relationship?: RelationshipType; // RelationshipType enum value
  gender?: GenderType; // GenderType enum value
  other_attrs?: Record<string, any> | null; // Pet or extensible attributes
  family_id?: string; // Added for permissions payload extraction
  avatar?: string;
  nickname?: string;
  lastAccessDate?: string;
}

export interface MemberInfo {
  isExperienced?: boolean;
  family_member_type?: string;
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: string;
  dobYear?: number;
  current_family?: string; // Add current_family property
  dobMonth?: number;
  dobDay?: number;
  email: string;
  dialCode: string;
  mobile: string;
  status: MemberStatus;
  lastLogin: string;
  createdAt?: string;
  subExpired?: boolean;
  roleType?: RoleType;
  memberType?: MemberType; // 'H' for human, 'P' for pet
  derivedMemberType?: DerivedMemberType;
  relationship?: RelationshipType; // RelationshipType enum value
  gender?: GenderType; // GenderType enum value
  other_attrs?: Record<string, any> | null; // Pet or extensible attributes
  familyId?: string; // Added for permissions payload extraction
  nickname?: string;
  avatar?: string;
  emergencyCardReady?: boolean;
  onBehalf: boolean;
  registered_member: boolean;
  registration_status: string;
  isSuspended: boolean;
  role_id: string;
  lastAccessDate?: string;
}

export interface UpdateMemberContactRequest {
  email?: string;
  dialCode?: string;
  mobile?: string;
}
